import React, { useContext, useMemo } from 'react';

import { Grid } from '@gogame/components';
import {
  FormContext,
  FormField,
  withFormFieldGroupFormField,
  getIn,
} from '@gogame/form';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import ChannelsFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/ChannelsField';
import ColorFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/ColorField';
import CreativePreview from 'scenes/Gallery/GalleryPage/Creatives/components/CreativePreview';
import CreativeTypeFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/CreativeTypeField';
import {
  DesignerAndOutsourceStudioProvider,
  StudioType,
  StudioComponent,
} from 'scenes/Gallery/GalleryPage/Creatives/components/DesignerAndOutsourceStudio';
import EnhancerFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/EnhancersField';
import OSFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/OSField';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  loadExclusiveForAffiliates,
  loadLanguages,
  loadNetworks,
  loadTested,
  loadProductionTypes,
} from 'scenes/Gallery/GalleryPage/Creatives/sources/fields';
import {
  CREATIVE_TYPE_CINEMAGRAPH,
  CREATIVE_TYPE_VIDEO,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

const Creative = ({ name, fileInfo }) => {
  const { values } = useContext(FormContext);
  const {
    isAllowDesigner,
    isAllowOutsourceStudio,
    isAllowExclusiveForAffiliates,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();

  const { [CREATIVE.CREATIVE_TYPE]: creativeTypeId } = useMemo(() => {
    return getIn(values, name) || {};
  }, [name, values]);

  const isVisibleYoutubeLink = useMemo(() => {
    return [CREATIVE_TYPE_VIDEO, CREATIVE_TYPE_CINEMAGRAPH].includes(
      creativeTypeId
    );
  }, [creativeTypeId]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={6} container>
        <CreativePreview
          creativeType={creativeTypeId}
          fileInfo={fileInfo}
          maxWidth={428}
          maxHeight={300}
        />
      </Grid>
      <Grid item xs={6}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <CreativeTypeFormComponent
              label={'Creative type'}
              name={CREATIVE.CREATIVE_TYPE}
              depends={[`${name}.${CREATIVE.NETWORKS}`]}
              fileInfo={fileInfo}
              required={true}
              padding={0}
            />
          </Grid>
          <Grid item xs={6}>
            <ColorFormComponent
              creativeType={creativeTypeId}
              name={CREATIVE.COLOR}
              padding={0}
            />
          </Grid>
          <Grid item xs={12}>
            <FormField.MultiSelect
              label={'Networks'}
              name={CREATIVE.NETWORKS}
              source={loadNetworks}
              depends={[`${CONCEPT.APPLICATIONS}`]}
              required={true}
              padding={0}
            />
          </Grid>
          <Grid item xs={12}>
            <ChannelsFormComponent
              creativeType={creativeTypeId}
              name={CREATIVE.CHANNELS}
              padding={0}
            />
          </Grid>
          {isAllowExclusiveForAffiliates ? (
            <Grid item xs={12}>
              <FormField.MultiSelect
                label={'Exclusive for affiliates'}
                name={CREATIVE.EXCLUSIVE_FOR_AFFILIATES}
                source={loadExclusiveForAffiliates}
                padding={0}
              />
            </Grid>
          ) : null}
          <Grid item xs={6}>
            <OSFormComponent
              creativeType={creativeTypeId}
              name={CREATIVE.OS}
              padding={0}
            />
          </Grid>
          <Grid item xs={6}>
            <FormField.Select
              label={'Language'}
              name={CREATIVE.LANGUAGE}
              source={loadLanguages}
              required={true}
              padding={0}
            />
          </Grid>
          {isAllowProductionType ? (
            <Grid item xs={6}>
              <FormField.Select
                label={'Production type'}
                name={CREATIVE.PRODUCTION_TYPE}
                source={loadProductionTypes}
                required={true}
                padding={0}
              />
            </Grid>
          ) : null}
          <Grid item xs={6}>
            <EnhancerFormComponent
              creativeType={creativeTypeId}
              name={CREATIVE.ENHANCERS}
              padding={0}
            />
          </Grid>
          <Grid item xs={6}>
            <FormField.Checkbox
              label={'AI Generated'}
              name={CREATIVE.AI_GENERATED}
              padding={0}
            />
          </Grid>
        </Grid>
      </Grid>
      {isAllowTested ? (
        <Grid item xs={3}>
          <FormField.Select
            label={'Tested'}
            name={CREATIVE.TESTED}
            source={loadTested}
            required={true}
            padding={0}
          />
        </Grid>
      ) : null}
      <Grid item xs={3}>
        <FormField.TextField
          label={'Sound ID'}
          thousandSeparator={false}
          name={CREATIVE.SOUND_ID}
          type={'number'}
          padding={0}
        />
      </Grid>
      <DesignerAndOutsourceStudioProvider path={name}>
        {isAllowDesigner && (
          <Grid item xs={3}>
            <StudioType />
          </Grid>
        )}
        {isAllowOutsourceStudio && (
          <Grid item xs={3}>
            <StudioComponent />
          </Grid>
        )}
      </DesignerAndOutsourceStudioProvider>
      {isVisibleYoutubeLink && (
        <Grid item xs={12}>
          <FormField.TextField
            label={'YouTube URL'}
            name={CREATIVE.YOUTUBE_LINK}
            padding={0}
          />
        </Grid>
      )}
    </Grid>
  );
};
export default withFormFieldGroupFormField({
  padding: 0,
})(Creative);
